/**
 * Timezone-safe date formatting utilities
 *
 * CRITICAL: Date-only strings (YYYY-MM-DD) from the database must not be
 * parsed with `new Date()` as it treats them as UTC midnight, causing
 * timezone-dependent display shifts (e.g., PST users see dates 1 day earlier).
 *
 * All formatters parse by components to avoid timezone interpretation.
 */

/**
 * Format a date-only string (YYYY-MM-DD) to "MMM YYYY" format
 * Used for member-since dates
 *
 * @example formatMemberSince("2024-01-15") => "Jan 2024"
 */
export function formatMemberSince(dateStr: string | null): string | null {
  if (!dateStr) return null;

  const [year, month] = dateStr.split('-').map(Number);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return `${monthNames[month - 1]} ${year}`;
}

/**
 * Format a date-only string (YYYY-MM-DD) to full date format
 * Used for booking dates without weekday
 *
 * @example formatDate("2024-01-15") => "January 15, 2024"
 */
export function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return `${monthNames[month - 1]} ${day}, ${year}`;
}

/**
 * Format a date-only string (YYYY-MM-DD) to long format with weekday
 * Used for appointment dates on booking cards
 *
 * @example formatDateLong("2024-01-15") => "Monday, January 15, 2024"
 */
export function formatDateLong(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Create date in local timezone for day-of-week calculation
  const date = new Date(year, month - 1, day);
  const dayOfWeek = dayNames[date.getDay()];

  return `${dayOfWeek}, ${monthNames[month - 1]} ${day}, ${year}`;
}

/**
 * Format a time string (HH:MM:SS) to 12-hour format
 * Used for all time display (booking times, appointment times)
 *
 * @example formatTime("14:30:00") => "2:30 PM"
 */
export function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}
